import {
  FOLLOWUP_MODE_SOURCE,
  findConditionRuleProblem,
  GOAL_HAS_MANDATE_SOURCE,
  GOAL_HAS_RISK_SOURCE,
  GOAL_RISK_SCORE_CAPPED_SOURCE,
  GOAL_RISK_SCORE_SOURCE,
  GOAL_TYPE_SOURCE,
  isConditionAllowedInScope,
  isOptionFieldType,
  isProductAssessmentSource,
  isQuizResultSource,
  isScoreConditionSource,
  isValueRequiredOperator,
  menuConditionOperator,
  operatorForNewSource,
  PORTFOLIO_HAS_MANDATE_CHECK_SOURCE,
  PORTFOLIO_MANDATE_STATUS_SOURCE,
  PORTFOLIO_MODEL_DEVIATION_SOURCE,
  PORTFOLIO_SCHEDULE_SOURCE,
  PORTFOLIO_TYPE_SOURCE,
  pdfSectionScopeToConditionScope,
  QUIZ_RESULT_SOURCE_PREFIX,
  RISK_SCORE_CAP_STATES,
  type RiskScoreCapState,
  SENTINEL_SOURCE_SCOPES,
  scalarConditionOperators,
} from "@repo/shared";
import {
  type ConfiguredAssessmentType,
  type FlowFieldOption,
  type FlowQuizSummary,
} from "@repo/shared/types";
import { useEffect, useState } from "react";
import {
  assessmentSourceValueOptions,
  buildAssessmentPickerEntries,
  buildQuizScorePickerEntries,
  quizResultValueOptions,
  scoreSourcePassScore,
} from "@/components/conditional-display/assessment-picker-entries";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { GoalTypeConditionPicker } from "@/components/conditional-display/goal-type-condition-picker";
import { ScalarOperatorItems } from "@/components/conditional-display/scalar-operator-items";
import { ScoreConditionValueInput } from "@/components/conditional-display/score-condition-value-input";
import { useCountryOptions } from "@/components/country-select";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useUpdateLayout } from "@/lib/hooks/use-pdf-templates";
import { useGoalTypes } from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { T } from "./layout-settings-dialog.i18n";
import type { ConditionalOperator, LayoutConfig, PdfLayout } from "./types";

type FlowField = {
  id: string;
  name: string;
  type: string;
  // Option rows with stable ids (QL-201); rule values reference the id.
  options: FlowFieldOption[];
  /** `flow_field.scope` — gates the field in the condition picker (QL-161). */
  scope?: "global" | "goal" | "portfolio";
};

type LayoutSettingsDialogProps = {
  open: boolean;
  layout: PdfLayout | null;
  availableFields: FlowField[];
  /** QPBI-321 — portfolio-scoped sources are only offered in per_portfolio sections. */
  sectionScope?: "global" | "per_goal" | "per_portfolio";
  portfolioCollectionId?: string | null;
  /** QL-361 — the configured assessment types of this template's flow. */
  configuredAssessmentTypes?: ConfiguredAssessmentType[];
  /** QL-361 — while true the list is a placeholder (loading or failed), which must not read as "all stale". */
  assessmentTypesUnknown?: boolean;
  /** QL-361 — the flow's quizzes, for the quiz status and score sources. */
  quizzes?: FlowQuizSummary[];
  /** QL-13 — while true the quiz list is a placeholder (loading or failed), which must not read as "all dangling". */
  quizzesUnknown?: boolean;
  templateLanguage: string;
  onClose: (success: boolean) => void;
};

export function LayoutSettingsDialog({
  open,
  layout,
  availableFields,
  sectionScope,
  portfolioCollectionId = null,
  configuredAssessmentTypes = [],
  assessmentTypesUnknown = false,
  quizzes: activeQuizzes = [],
  quizzesUnknown = false,
  templateLanguage,
  onClose,
}: LayoutSettingsDialogProps) {
  const { userLanguage, tenantDefaultLanguage } = useUserLanguage();
  const t = useT(T);
  const countryOptions = useCountryOptions();
  const updateLayoutMutation = useUpdateLayout();
  const [error, setError] = useState<string | null>(null);

  // Conditional display state
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [sourceFieldId, setSourceFieldId] = useState("");
  const [storedOperator, setOperator] = useState<ConditionalOperator>("equals");
  const [conditionValue, setConditionValue] = useState("");

  // Clear conditionValue when sourceFieldId changes (different fields have different valid values)
  // and keep the operator valid for the new source.
  const handleSourceFieldChange = (newFieldId: string) => {
    setSourceFieldId(newFieldId);
    setConditionValue("");
    const fieldType = availableFields.find((f) => f.id === newFieldId)?.type ?? "";
    setOperator(operatorForNewSource(operator, { sourceId: newFieldId, fieldType }));
  };

  // QL-120 — the goal-type source is only offered where a current goal exists
  // at render time (per_goal / per_portfolio sections), and only when the
  // flow's collection defines goal types. Offered while loading so an existing
  // rule's source does not flash as unrecognised.
  const { goalTypes: collectionGoalTypes, isLoading: goalTypesLoading } = useGoalTypes(
    portfolioCollectionId ?? null,
  );
  // QL-161: a condition may only read sources evaluable at the enclosing
  // section's scope (global < goal < portfolio) — same registry + predicate
  // as the server-side guard, so the picker and the reject path agree.
  const containerScope = pdfSectionScopeToConditionScope(sectionScope ?? "global");
  const offerSentinel = (sourceId: string) =>
    isConditionAllowedInScope(SENTINEL_SOURCE_SCOPES[sourceId] ?? "global", containerScope);
  const conditionFields = availableFields.filter((f) =>
    isConditionAllowedInScope(f.scope ?? "global", containerScope),
  );
  const canOfferGoalType =
    offerSentinel(GOAL_TYPE_SOURCE) &&
    !!portfolioCollectionId &&
    (goalTypesLoading || collectionGoalTypes.length > 0);

  useEffect(() => {
    if (open && layout) {
      // Parse existing config
      try {
        const config: LayoutConfig = layout.config ? JSON.parse(layout.config) : {};

        // Load conditional display settings
        if (config.conditionalDisplay) {
          setConditionalEnabled(config.conditionalDisplay.enabled || false);
          setSourceFieldId(config.conditionalDisplay.sourceFieldId || "");
          setOperator(config.conditionalDisplay.operator || "equals");
          setConditionValue(config.conditionalDisplay.value || "");
        } else {
          setConditionalEnabled(false);
          setSourceFieldId("");
          setOperator("equals");
          setConditionValue("");
        }
      } catch (err) {
        log.error({ err }, "failed to parse pdf layout config");
        setConditionalEnabled(false);
        setSourceFieldId("");
        setOperator("equals");
        setConditionValue("");
      }
    } else {
      // Reset when closing
      setConditionalEnabled(false);
      setSourceFieldId("");
      setOperator("equals");
      setConditionValue("");
    }
    setError(null);
  }, [layout, open]);

  // Get options for dropdown fields
  type FieldOption = string | { label: string; value: string };

  const getFieldOptions = (fieldId: string): FieldOption[] => {
    // QL-361 — Passed / Not passed / Not answered, or Incomplete for Combined.
    if (isProductAssessmentSource(fieldId)) {
      return assessmentSourceValueOptions(fieldId, templateLanguage);
    }
    // QL-13 — the three quiz statuses; rules compare the raw status string.
    if (isQuizResultSource(fieldId)) {
      return quizResultValueOptions(templateLanguage);
    }
    // Special field: Classification Type
    if (fieldId === "__classification_type__") {
      return ["Retail", "Professional", "Eligible Counterparty"];
    }
    // Special field: Client Country
    if (fieldId === "__client_country__") {
      // QL-323 — values are the ISO codes the client record holds (a name
      // could never equal `client.country`); labels localized and sorted with
      // the Nordic markets first, the same list the client form uses.
      return [...countryOptions.priority, ...countryOptions.rest].map((o) => ({
        value: o.code,
        label: o.label,
      }));
    }
    // Portfolio-scoped field: Portfolio Type (QPBI-321)
    if (fieldId === PORTFOLIO_TYPE_SOURCE) {
      return [
        { label: "Model Portfolio", value: "model" },
        { label: "Custom Portfolio", value: "custom" },
        { label: "Dynamic Portfolio", value: "dynamic" },
      ];
    }
    // Portfolio-scoped field: Schedule Type (QPBI-321)
    if (fieldId === PORTFOLIO_SCHEDULE_SOURCE) {
      return [
        { label: "One-time", value: "one_time" },
        { label: "Recurring", value: "recurring" },
      ];
    }
    // QL-375 group 10b — the portfolio's own mandate verdict
    if (fieldId === PORTFOLIO_HAS_MANDATE_CHECK_SOURCE) {
      return [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ];
    }
    // QL-72 — model portfolio picked from another risk score
    if (fieldId === PORTFOLIO_MODEL_DEVIATION_SOURCE) {
      return [
        { label: t.modelDeviationYes, value: "yes" },
        { label: t.modelDeviationNo, value: "no" },
        { label: t.modelDeviationNotModel, value: "not_model" },
      ];
    }
    if (fieldId === PORTFOLIO_MANDATE_STATUS_SOURCE) {
      return [
        { label: "Within range", value: "ok" },
        { label: "Deviation accepted", value: "accepted" },
        { label: "Breach", value: "deviation" },
        { label: "Blocked", value: "not_accepted" },
        { label: "Not checked", value: "unchecked" },
      ];
    }
    // Special field: Has Risk Assessment
    if (fieldId === "__goal_has_risk__") {
      return [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ];
    }
    // QL-375 group 10 — goal has a mandate in force
    if (fieldId === GOAL_HAS_MANDATE_SOURCE) {
      return [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ];
    }
    // Goal-scoped field: Risk Score Capped (QL-71)
    if (fieldId === GOAL_RISK_SCORE_CAPPED_SOURCE) {
      // The four states, ordered as the shared const declares them. The
      // exhaustive map means a fifth state added upstream fails to compile here
      // rather than silently shipping an unlabelled option.
      const capStateLabels: Record<RiskScoreCapState, string> = {
        yes: t.capStateYes,
        no: t.capStateNo,
        no_risk_assessment: t.capStateNoRiskAssessment,
        no_client_field_data: t.capStateNoClientFieldData,
      };
      return RISK_SCORE_CAP_STATES.map((s) => ({ value: s, label: capStateLabels[s] }));
    }
    // QL-774 — the PDF builder seeds "true" / "false" for every instance.
    if (fieldId === FOLLOWUP_MODE_SOURCE) {
      return [
        { label: t.capStateYes, value: "true" },
        { label: t.capStateNo, value: "false" },
      ];
    }
    if (fieldId === "__expecting_financial_changes__") {
      return [
        { label: "Unanswered", value: "unanswered" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    if (fieldId === "__trapets_pep__") {
      return [
        { label: "Unchecked", value: "unchecked" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    // Special field: Trapets Sanctioned Status
    if (fieldId === "__trapets_sanctioned__") {
      return [
        { label: "Unchecked", value: "unchecked" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    // QL-68 — Sumsub verified status, the same three values the flow picker
    // offers and the PDF field builder seeds (`sumsub_verified`).
    if (fieldId === "__sumsub_verified__") {
      return [
        { label: "Unchecked", value: "unchecked" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    // QL-68 — Sumsub document country: same localised list as Client Country,
    // valued by the alpha-3 code Sumsub reports (raw comparison, no table).
    if (fieldId === "__sumsub_document_country__") {
      return [...countryOptions.priority, ...countryOptions.rest].map((o) => ({
        value: o.alpha3,
        label: o.label,
      }));
    }

    const field = availableFields.find((f) => f.id === fieldId);
    if (field?.type === "boolean") {
      return [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ];
    }
    if (!field || !isOptionFieldType(field.type)) {
      return [];
    }

    // Rule values store the option id (QL-201); show the label. Archived
    // options stay listed only while the rule still uses them.
    return (field.options ?? [])
      .filter((o) => !o.isArchived || o.id === conditionValue)
      .map((o) => ({ value: o.id, label: o.label }));
  };

  // Helper to get option value (handles both string and object options)
  const getOptionValue = (option: FieldOption): string =>
    typeof option === "string" ? option : option.value;

  // Helper to get option label (handles both string and object options)
  const getOptionLabel = (option: FieldOption): string =>
    typeof option === "string" ? option : option.label;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!layout) return;

    setError(null);

    try {
      // Build config object
      const config: LayoutConfig = {};

      // Add conditional display if enabled
      if (conditionalEnabled) {
        const ruleProblem = findConditionRuleProblem(
          { sourceFieldId, operator, value: conditionValue },
          {
            quizzes: quizzesUnknown ? null : activeQuizzes,
            assessmentTypes: assessmentTypesUnknown ? null : configuredAssessmentTypes,
          },
        );
        if (ruleProblem) {
          setError(conditionRuleProblemMessage(ruleProblem, userLanguage));
          return;
        }

        config.conditionalDisplay = {
          enabled: true,
          sourceFieldId,
          operator,
          value:
            operator === "is_empty" || operator === "is_not_empty" ? undefined : conditionValue,
        };
      } else {
        config.conditionalDisplay = {
          enabled: false,
          sourceFieldId: "",
          operator: "equals",
        };
      }

      await updateLayoutMutation.mutateAsync({
        layoutId: layout.id,
        data: { config: JSON.stringify(config) },
      });

      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToUpdate);
    }
  };

  const sourceField = availableFields.find((f) => f.id === sourceFieldId);

  // QL-361 — score sources compare a number: Equals / Greater than / Less than
  // only, no "is empty" pair ("has the client answered" is a status question).
  const isScoreSource = isScoreConditionSource(sourceFieldId);

  // Fields that should only show equals/not_equals (no numeric comparisons)
  const isEnumField =
    sourceFieldId === FOLLOWUP_MODE_SOURCE ||
    sourceFieldId === "__classification_type__" ||
    sourceFieldId === "__client_country__" ||
    sourceFieldId === PORTFOLIO_TYPE_SOURCE ||
    sourceFieldId === PORTFOLIO_SCHEDULE_SOURCE ||
    sourceFieldId === PORTFOLIO_HAS_MANDATE_CHECK_SOURCE ||
    sourceFieldId === PORTFOLIO_MANDATE_STATUS_SOURCE ||
    sourceFieldId === PORTFOLIO_MODEL_DEVIATION_SOURCE ||
    sourceFieldId === "__goal_has_risk__" ||
    sourceFieldId === GOAL_HAS_MANDATE_SOURCE ||
    sourceFieldId === GOAL_RISK_SCORE_CAPPED_SOURCE ||
    sourceFieldId === "__expecting_financial_changes__" ||
    sourceFieldId === "__trapets_pep__" ||
    sourceFieldId === "__trapets_sanctioned__" ||
    sourceFieldId === "__sumsub_verified__" ||
    sourceFieldId === "__sumsub_document_country__" ||
    isQuizResultSource(sourceFieldId) ||
    isProductAssessmentSource(sourceFieldId) ||
    sourceField?.type === "boolean";
  // A score source gets its own menu even when an enum rule above covers it.
  const scalarOperators = scalarConditionOperators(sourceFieldId, isEnumField, sourceField?.type);

  // Multiselect answers are sets — membership operators instead of equality
  const isMultiselectField = sourceField?.type === "multiselect";

  // Boolean answers are Yes/No — equality only, values from a fixed list
  const isBooleanField = sourceField?.type === "boolean";

  const isDropdownField =
    isOptionFieldType(sourceField?.type ?? "") || isBooleanField || isEnumField;
  const dropdownOptions = sourceFieldId ? getFieldOptions(sourceFieldId) : [];
  const isGoalTypeSource = sourceFieldId === GOAL_TYPE_SOURCE;
  // QL-237 / QL-254 — a stored rule whose operator the menu no longer lists
  // shows, and saves, the one a switch to its source would pick.
  const operator =
    isGoalTypeSource || isMultiselectField
      ? storedOperator
      : menuConditionOperator(storedOperator, scalarOperators, {
          sourceId: sourceFieldId,
          fieldType: sourceField?.type ?? "",
        });
  const needsValue = isGoalTypeSource
    ? operator === "in" || operator === "not_in"
    : isMultiselectField
      ? operator === "contains" || operator === "not_contains"
      : scalarOperators.includes(operator) && isValueRequiredOperator(operator);

  // QL-323 — the source picker is searchable: a flat option list with the same
  // entries, in the same order, the grouped Select used to render.
  const sourceOptions: SearchableSelectOption[] = [];
  const offer = (value: string, label: string, group?: string) =>
    sourceOptions.push({ value, label, group });
  offer(FOLLOWUP_MODE_SOURCE, t.followupMode);
  offer("__client_country__", t.clientCountry);
  offer("__classification_type__", t.classificationType);
  offer("__trapets_pep__", t.trapetsPep);
  offer("__trapets_sanctioned__", t.trapetsSanctioned);
  offer("__sumsub_verified__", t.sumsubVerified);
  offer("__sumsub_document_country__", t.sumsubDocumentCountry);
  offer("__expecting_financial_changes__", t.expectingFinancialChanges);
  // Goal-scoped sources — only inside per_goal / per_portfolio sections (QL-161)
  if (offerSentinel(GOAL_RISK_SCORE_SOURCE)) offer(GOAL_RISK_SCORE_SOURCE, t.goalRiskScore);
  if (offerSentinel(GOAL_HAS_RISK_SOURCE)) offer(GOAL_HAS_RISK_SOURCE, t.goalHasRisk);
  if (offerSentinel(GOAL_HAS_MANDATE_SOURCE)) offer(GOAL_HAS_MANDATE_SOURCE, t.goalHasMandate);
  if (offerSentinel(GOAL_RISK_SCORE_CAPPED_SOURCE))
    offer(GOAL_RISK_SCORE_CAPPED_SOURCE, t.goalRiskScoreCapped);
  // Goal-type source — only inside per_goal / per_portfolio sections of a
  // template whose collection has goal types (QL-120)
  if (canOfferGoalType) offer(GOAL_TYPE_SOURCE, t.goalType);
  // Portfolio-scoped sources — only inside per_portfolio sections, where the
  // current portfolio exists (QPBI-321)
  if (offerSentinel(PORTFOLIO_TYPE_SOURCE)) {
    offer(PORTFOLIO_TYPE_SOURCE, t.portfolioType);
    offer(PORTFOLIO_SCHEDULE_SOURCE, t.portfolioScheduleType);
    // QL-375 group 10b
    offer(PORTFOLIO_HAS_MANDATE_CHECK_SOURCE, t.portfolioHasMandateCheck);
    offer(PORTFOLIO_MANDATE_STATUS_SOURCE, t.portfolioMandateStatus);
    // QL-72
    offer(PORTFOLIO_MODEL_DEVIATION_SOURCE, t.portfolioModelDeviation);
  }
  // QL-13 — quiz results; global scope, offered in every section
  for (const quiz of activeQuizzes) {
    offer(`${QUIZ_RESULT_SOURCE_PREFIX}${quiz.id}`, `${t.quizField} ${quiz.name}`);
  }
  // QL-361 — quiz scores, one per quiz; global scope like the results
  for (const entry of buildQuizScorePickerEntries(activeQuizzes, userLanguage)) {
    offer(entry.id, entry.label);
  }
  for (const field of conditionFields) {
    offer(field.id, `${t.flowField} ${field.name} (${field.type})`);
  }
  // QL-361 — product-assessment statuses and scores; global scope, offered in every section.
  // Offered last: it is the only grouped run, and SearchableSelect never closes a heading, so
  // any ungrouped entry after it would render under "Product assessment".
  for (const entry of buildAssessmentPickerEntries(configuredAssessmentTypes, {
    uiLanguage: userLanguage,
    nameLanguage: templateLanguage,
    fallbackLanguage: tenantDefaultLanguage,
    valueLanguage: templateLanguage,
  })) {
    offer(entry.id, entry.label, entry.group);
  }

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {layout?.layoutType === "same_page" ? t.samePageBlockSettings : t.blockSettings}
          </DialogTitle>
          <DialogDescription>{t.blockSettingsDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            {/* Conditional Display */}
            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="conditional-enabled">{t.conditionalDisplay}</Label>
                  <p className="text-sm text-muted-foreground">{t.showBlockWhen}</p>
                </div>
                <Switch
                  id="conditional-enabled"
                  checked={conditionalEnabled}
                  onCheckedChange={setConditionalEnabled}
                  disabled={updateLayoutMutation.isPending}
                />
              </div>

              {conditionalEnabled && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="source-field">
                      {t.when} <span className="text-destructive">*</span>
                    </Label>
                    <SearchableSelect
                      id="source-field"
                      value={sourceFieldId}
                      onValueChange={handleSourceFieldChange}
                      options={sourceOptions}
                      placeholder={t.selectField}
                      searchPlaceholder={t.searchFields}
                      emptyMessage={t.noFieldsAvailable}
                      noResultsMessage={t.noFieldsMatch}
                      disabled={updateLayoutMutation.isPending}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="operator">
                      {t.condition} <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={operator}
                      onValueChange={(value) => setOperator(value as ConditionalOperator)}
                      disabled={updateLayoutMutation.isPending}
                    >
                      <SelectTrigger id="operator">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isGoalTypeSource ? (
                          // QL-120 — set operators only; a single tick is the equality case.
                          <>
                            <SelectItem value="in">{t.isOneOf}</SelectItem>
                            <SelectItem value="not_in">{t.isNotOneOf}</SelectItem>
                          </>
                        ) : isMultiselectField ? (
                          <>
                            <SelectItem value="contains">{t.contains}</SelectItem>
                            <SelectItem value="not_contains">{t.notContains}</SelectItem>
                            <SelectItem value="is_empty">{t.isEmpty}</SelectItem>
                            <SelectItem value="is_not_empty">{t.isNotEmpty}</SelectItem>
                          </>
                        ) : (
                          <ScalarOperatorItems operators={scalarOperators} />
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {needsValue && (
                    <div className="space-y-2">
                      <Label htmlFor="condition-value">
                        {t.value} <span className="text-destructive">*</span>
                      </Label>
                      {isGoalTypeSource && portfolioCollectionId ? (
                        <GoalTypeConditionPicker
                          portfolioCollectionId={portfolioCollectionId}
                          value={conditionValue}
                          onChange={setConditionValue}
                          language={templateLanguage}
                          disabled={updateLayoutMutation.isPending}
                        />
                      ) : isScoreSource ? (
                        <ScoreConditionValueInput
                          id="condition-value"
                          value={conditionValue}
                          onChange={setConditionValue}
                          passScore={scoreSourcePassScore(
                            sourceFieldId,
                            configuredAssessmentTypes,
                            activeQuizzes,
                          )}
                          language={userLanguage}
                          disabled={updateLayoutMutation.isPending}
                        />
                      ) : sourceFieldId === "__client_country__" ||
                        sourceFieldId === "__sumsub_document_country__" ? (
                        <SearchableSelect
                          id="condition-value"
                          value={conditionValue}
                          onValueChange={setConditionValue}
                          options={dropdownOptions.map((option) => ({
                            value: getOptionValue(option),
                            label: getOptionLabel(option),
                          }))}
                          placeholder={t.selectValue}
                          searchPlaceholder={t.searchCountries}
                          noResultsMessage={t.noCountriesMatch}
                          disabled={updateLayoutMutation.isPending}
                        />
                      ) : isDropdownField && dropdownOptions.length > 0 ? (
                        <Select
                          value={conditionValue}
                          onValueChange={setConditionValue}
                          disabled={updateLayoutMutation.isPending}
                        >
                          <SelectTrigger id="condition-value">
                            <SelectValue placeholder={t.selectValue} />
                          </SelectTrigger>
                          <SelectContent>
                            {dropdownOptions.map((option) => (
                              <SelectItem
                                key={getOptionValue(option)}
                                value={getOptionValue(option)}
                              >
                                {getOptionLabel(option)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : sourceField?.type === "date" ? (
                        // QL-209 — same picker as the date answer, so the value is yyyy-MM-dd
                        <DatePicker
                          id="condition-value"
                          value={conditionValue}
                          onChange={setConditionValue}
                          disabled={updateLayoutMutation.isPending}
                          required
                        />
                      ) : sourceField?.type === "datetime" ? (
                        // QL-211 — same picker as the datetime answer, so the value is UTC
                        <DateTimePicker
                          id="condition-value"
                          value={conditionValue}
                          onChange={setConditionValue}
                          disabled={updateLayoutMutation.isPending}
                          required
                        />
                      ) : (
                        <Input
                          id="condition-value"
                          value={conditionValue}
                          onChange={(e) => setConditionValue(e.target.value)}
                          placeholder={t.enterValue}
                          disabled={updateLayoutMutation.isPending}
                          required
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <DialogCancel disabled={updateLayoutMutation.isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={updateLayoutMutation.isPending}>
              {t.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
